import { useKeycloak } from "@react-keycloak/web";
import { Button, Form, Input, Select, Space, Switch } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CardTitle from "../../../../components/common/CardTitle";
import CustomCard from "../../../../components/common/CustomCard";
import SuccessModal from "../../../../components/common/SuccessModal";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import Loader from "../../../../components/ui/Loader";
import { useGetAllCompanyQuery } from "../../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useGetPortalSingleSubDepartmentByIdQuery,
  useGetProtalAllDepartmentQuery,
} from "../../../../features/portal/settings/settingsApi";
import {
  useCreateUserMutation,
  useGetSingleUserQuery,
  useGetUserStatusQuery,
  useUpdateSingleUserMutation,
  useUpdateUserStatusMutation,
} from "../../../../features/portal/user/userApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";

export default function AddEditNewUser() {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);
  const { open, handleOpen, handleClose } = useModal<boolean>();
  const [form] = Form.useForm();
  const backLink = "/admin/users-roll-access/user-list";
  const { id } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { user, role } = useAppSelector((state) => state.auth);

  const isAdmin2 = keycloak?.realmAccess?.roles?.includes("admin2");
  const canEditEmail =
    role === "SUPER_ADMIN" ||
    keycloak?.realmAccess?.roles?.includes("super_admin");
  const { isError, error, data } = useGetAllCompanyQuery({
    params: {
      company_id: isAdmin2 ? user?.data?.company_id : undefined,
      page: 1,
      limit: 1000000000000,
    },
  });
  const [
    createUser,
    {
      isError: isCreateUserError,
      error: createUserError,
      isLoading: isCreateUserLoading,
      isSuccess: isCreateUserSuccess,
    },
  ] = useCreateUserMutation();
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetSingleUserQuery(id || "", {
    skip: !id,
  });
  const [
    updateSingleUser,
    {
      isError: updateSingleUserIsError,
      error: updateSingleUserError,
      isSuccess: updateSingleUserIsSuccess,
      isLoading: updateSingleUserIsLoading,
    },
  ] = useUpdateSingleUserMutation();
  const [
    updateUserStatus,
    {
      isError: updateUserStatusIsError,
      error: updateUserStatusError,
      isSuccess: updateUserStatusIsSuccess,
      isLoading: updateUserStatusIsLoading,
    },
  ] = useUpdateUserStatusMutation();
  const {
    data: getPortalDepartmentData,
    isError: getPortalDepartmentIsError,
    error: getPortalDepartmentError,
    isLoading: getPortalDepartmentIsLoading,
  } = useGetProtalAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000,
      is_published: 1,
    },
  });
  type StatusDataType = {
    data?: {
      status?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };

  const {
    data: getStatusData,
    isError: getStatusIsError,
    error: getStatusError,
    isLoading: getStatusIsLoading,
  } = useGetUserStatusQuery(detailsData?.data?.email || "", {
    skip: !detailsData?.data?.email,
  }) as {
    data?: StatusDataType;
    isError: boolean;
    error: any;
    isLoading: boolean;
  };

  const {
    data: getPortalSubDepartmentData,
    isError: getPortalSubDepartmentIsError,
    error: getPortalSubDepartmentError,
    isLoading: getPortalSubDepartmentIsLoading,
  } = useGetPortalSingleSubDepartmentByIdQuery(
    {
      params: {
        skip: undefined,
        page: 1,
        limit: 100000,
        status: true,
        department_id: selectedDepartmentId ?? undefined,
      },
    },
    { skip: !selectedDepartmentId }
  );
  useError(isError, error, "Get all sub department data failed");
  useError(getStatusIsError, getStatusError, "Get user status failed");
  useError(
    updateUserStatusIsError,
    updateUserStatusError,
    "update user status failed"
  );

  useError(updateSingleUserIsError, updateSingleUserError, "User updated fail");
  useError(isDetailsError, detailsError, "User details get fail");
  useError(isError, error, "Get all company fail");
  useError(isCreateUserError, createUserError, "User create fail");
  useError(
    getPortalDepartmentIsError,
    getPortalDepartmentError,
    "Get all department data failed"
  );
  useError(
    getPortalSubDepartmentIsError,
    getPortalSubDepartmentError,
    "Get all sub department data failed"
  );

  const onFinish = (values: any) => {
    if (id) {
      const newData = {
        username: values?.username,
        email: values?.email,
        company_id: values?.company_id,
        department_id: values?.department_id,
        sub_department_id: values?.sub_department_id,
      };
      // console.log("newData", newData);
      updateSingleUser({
        id: id,
        data: newData,
      });
    } else {
      createUser(values);
      // console.log("Create User:", values);
    }
  };

  const handleOkay = () => {
    handleClose();
    navigate(`/admin/users-roll-access/user-list`);
  };

  useEffect(() => {
    if (id && detailsData?.data) {
      form.setFieldsValue({
        username: detailsData?.data?.username,
        email: detailsData?.data?.email,
        company_id: detailsData?.data?.company_id,
        department_id: detailsData?.data?.department?.id,
        sub_department_id: detailsData?.data?.sub_department?.id,
      });
      setSelectedDepartmentId(detailsData?.data?.department?.id);
    }
  }, [id, detailsData?.data, setSelectedDepartmentId, form]);

  useEffect(() => {
    if (isCreateUserSuccess) {
      form.resetFields();
      handleOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateUserSuccess]);

  useEffect(() => {
    if (updateSingleUserIsSuccess) {
      form.resetFields();
      handleOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSingleUserIsSuccess]);

  useEffect(() => {
    if (updateUserStatusIsSuccess) {
      toast("success", "Status update successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserStatusIsSuccess]);

  return (
    <>
      {open?.type === "ADD" && (
        <SuccessModal
          open={open?.open}
          handleClose={handleClose}
          handleOkay={handleOkay}
          data={open?.data}
          title={`${
            id ? "User Updated Successfully!" : "New User Created Successfully!"
          }`}
        />
      )}

      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open?.open}
          handleClose={handleClose}
          handleOk={(checked) => {
            updateUserStatus({
              email: detailsData?.data?.email,
              enabled: checked,
            });
          }}
          data={open?.data}
          okLoading={updateUserStatusIsLoading}
          okText="Confirm"
          cancelText="Cancel"
          confirmTitle="Update Status"
          questionText="Are you sure you want to update the status?"
        />
      )}

      <SidebarLayout
        title={`${id ? "Edit User" : "Add New User"}`}
        backLink="/admin/users-roll-access/user-list"
      >
        {(id && isDetailsLoading) ||
        getPortalSubDepartmentIsLoading ||
        getPortalDepartmentIsLoading ? (
          <Loader />
        ) : (
          <Form
            className="custom-form"
            layout="vertical"
            requiredMark="optional"
            size="large"
            onFinish={onFinish}
            form={form}
          >
            <CustomCard className=" p-6 pb-0 rounded-b-none">
              <CardTitle
                rightSide={
                  !getStatusIsLoading &&
                  !!detailsData?.data?.email && (
                    <div>
                      <Switch
                        checked={getStatusData?.data?.enabled}
                        disabled={updateUserStatusIsLoading}
                        onChange={(checked) => {
                          handleOpen({
                            type: "CONFIRM",
                            data: checked,
                          });
                        }}
                      />
                    </div>
                  )
                }
              >
                User Info
              </CardTitle>

              <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-6 pt-4 mb-6">
                <Form.Item
                  label="User Name"
                  name="username"
                  rules={[
                    { required: true, message: "Please enter user name" },
                  ]}
                >
                  <Input placeholder="Enter User Name" allowClear />
                </Form.Item>

                <Form.Item
                  label="Email ID"
                  name="email"
                  validateFirst
                  rules={[
                    {
                      required: true,
                      message: "Please enter email id",
                    },
                    // {
                    //   validator: async (_: any, value: string) => {
                    //     if (!value) {
                    //       return Promise.resolve();
                    //     }

                    //     if (!mailRegex.test(value)) {
                    //       return Promise.reject(
                    //         new Error("Please enter a valid email")
                    //       );
                    //     }

                    //     if (!isValidTLD(value)) {
                    //       return Promise.reject(
                    //         new Error("Please enter a valid TLD")
                    //       );
                    //     }

                    //     return Promise.resolve();
                    //   },
                    // },
                  ]}
                >
                  <Input
                    placeholder="Enter email id"
                    allowClear
                    disabled={id ? !canEditEmail : false}
                  />
                </Form.Item>

                <Form.Item
                  label="Company Name"
                  name="company_id"
                  rules={[
                    { required: true, message: "Please enter a company name" },
                  ]}
                  initialValue={isAdmin2 ? user?.data?.company_id : undefined}
                >
                  <Select
                    placeholder="Company Name"
                    showSearch
                    options={
                      data?.data?.companies?.map((company: any) => ({
                        label: company?.company_name,
                        value: company?.id,
                      })) || []
                    }
                    className="w-full"
                    suffixIcon={<SelectDownIcon />}
                    style={{ width: "100%" }}
                    allowClear={!isAdmin2}
                    optionFilterProp="label"
                    disabled={isAdmin2}
                  />
                </Form.Item>

                {role === "SUPER_ADMIN" ? (
                  <></>
                ) : (
                  <>
                    <Form.Item
                      label="Department"
                      name="department_id"
                      // rules={[
                      //   {
                      //     required: false,
                      //     message: "Please enter a department",
                      //   },
                      // ]}
                    >
                      <Select
                        placeholder="Department"
                        showSearch
                        options={
                          getPortalDepartmentData?.data?.departments?.map(
                            (department: any) => ({
                              label: department?.name,
                              value: department?.id,
                            })
                          ) || []
                        }
                        onChange={(value) => {
                          setSelectedDepartmentId(value || null);
                          form.setFieldsValue({ sub_department_id: null });
                        }}
                        className="w-full"
                        suffixIcon={<SelectDownIcon />}
                        style={{ width: "100%" }}
                        allowClear
                        optionFilterProp="label"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Sub Department"
                      name="sub_department_id"
                      // rules={[
                      //   {
                      //     required: false,
                      //     message: "Please enter a sub department",
                      //   },
                      // ]}
                    >
                      <Select
                        placeholder="Sub Department"
                        showSearch
                        options={
                          getPortalSubDepartmentData?.data?.map(
                            (department: any) => ({
                              label: department?.name,
                              value: department?.id,
                            })
                          ) || []
                        }
                        className="w-full"
                        suffixIcon={<SelectDownIcon />}
                        style={{ width: "100%" }}
                        allowClear
                        optionFilterProp="label"
                        disabled={!selectedDepartmentId}
                      />
                    </Form.Item>
                  </>
                )}

                {/* <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    required: true,
                    message: "Please enter your password",
                  },
                ]}
              >
                <Input.Password placeholder="Password" allowClear />
              </Form.Item> */}
              </div>

              {/* <CardTitle>
              Select Products & User Role Group{" "}
              <span className="cursor-pointer">
                <AddIcon />
              </span>
            </CardTitle>
            <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-6 pt-4 mb-6">
              <Form.Item
                label="Select Products"
                name="products"
                rules={[{ required: true, message: "Please select product" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select products"
                  //   options={selectStringOption([
                  //     "Product 1",
                  //     "Product 2",
                  //     "Product 3",
                  //     "Product 4",
                  //   ])}
                  className="w-full"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item
                label="Select User Group"
                name="user_group"
                rules={[
                  { required: true, message: "Please select user group" },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select user group"
                  //   options={selectStringOption([
                  //     "Product 1",
                  //     "Product 2",
                  //     "Product 3",
                  //     "Product 4",
                  //   ])}
                  className="w-full"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div> */}
            </CustomCard>

            {/* sticky bottom-0 */}
            <div className="p-6 rounded-md rounded-t-none bg-white border border-t-0 border-light">
              <Space className="w-full mt-2" size={12}>
                <Link to={backLink}>
                  <Button
                    className="normal-dark-button modal-button"
                    size="middle"
                  >
                    Cancel
                  </Button>
                </Link>
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={id ? updateSingleUserIsLoading : isCreateUserLoading}
                >
                  {id ? "Update" : "Create user"}
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </SidebarLayout>
    </>
  );
}
